import React, { useState } from 'react';
import './total.css';
import {
  Button,
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Avatar,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/system';
import { useGetCandidateScheduledList, useDeleteScheduledCandidate } from '../../hooks/useCandidate';
import { useDeleteConfirmation } from '../../context/DeleteConfirmationContext';
import { formatDate } from '../../utils/dateFormat';
import ProgressModel from './ProgressModel';
import SheduleBox from './SheduleBox';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventIcon from '@mui/icons-material/Event';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  TH,
  TH_CENTER,
  TD,
  TD_CENTER,
  Row,
  AVATAR_SX,
  CHIP_SX,
  TableWrap,
  TABLE_STYLE,
} from '../common/tableStyles';

// ---- FORTHEYE soft-card tokens (shared with the employee portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff'; // primary blue
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

// Styled Components
const CandidateCard = styled(Card)(({ theme }) => ({
  marginBottom: '16px',
  borderRadius: '18px',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 12px 28px rgba(17,24,39,0.08)',
    transform: 'translateY(-2px)',
  },
}));

const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
  fontSize: '14px',
});

const ordinalLabel = (n) =>
  ({ 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', 5: '5th' }[n] || `${n}th`);

// Derive the candidate's current stage from their latest progress status.
// The final hiring outcome (joined / pending onboarding / dropped) takes priority.
const getCurrentStatus = (applicant) => {
  if (applicant.final_status === 'joined') {
    return { label: 'Joined', color: '#1b5e20', bg: '#e8f5e9' };
  }
  if (applicant.final_status === 'pending') {
    return { label: 'Pending', color: '#e65100', bg: '#fff3e0' };
  }
  if (applicant.final_status === 'rejected') {
    return { label: 'Dropped', color: '#c62828', bg: '#fdecea' };
  }

  const statuses = Array.isArray(applicant.statuses) ? applicant.statuses : [];
  if (!statuses.length) {
    return { label: 'Scheduled', color: '#475569', bg: '#f1f5f9' };
  }
  const latest = [...statuses].sort(
    (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
  )[0];
  switch (latest.status) {
    case 'selected':
      return { label: 'Selected', color: '#2e7d32', bg: '#e8f5e9' };
    case 'rejected':
      return { label: 'Rejected', color: '#d32f2f', bg: '#fdecea' };
    case 'move_to_next_round':
      return {
        label: `${ordinalLabel(latest.round_number)} Round`,
        color: '#1565c0',
        bg: '#e3f2fd',
      };
    default:
      return { label: '1st Round', color: '#475569', bg: '#f1f5f9' };
  }
};

const ScheduledCandidate = () => {
  const { data: sheduledlist } = useGetCandidateScheduledList();
  const deleteCandidate = useDeleteScheduledCandidate();
  const { showDeleteConfirmation } = useDeleteConfirmation();
  const [open, setOpen] = React.useState(false);
  const [selectedCan, setSelectedCan] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Show candidates still active in the interview process — freshly scheduled
  // AND those moved on to later rounds (they keep their round status here, e.g.
  // "2nd Round"). Only final outcomes leave this list: selected -> Selected,
  // rejected -> Rejected, or any final_status.
  const scheduledOnly = Array.isArray(sheduledlist)
    ? sheduledlist.filter(
        (a) =>
          !a.final_status &&
          !(a.statuses || []).some((s) =>
            ['selected', 'rejected'].includes(s.status)
          )
      )
    : [];

  const handleOpen = (applicant) => {
    setOpen(true);
    setSelectedCan(applicant);
  };

  // Delete a scheduled candidate after a confirm. Uses the shared confirm dialog;
  // the mutation removes the interview application (and its progress rows) and
  // refreshes the list.
  const handleDelete = (applicant) => {
    showDeleteConfirmation(applicant.name || 'this candidate', () => {
      deleteCandidate.mutate(applicant.id);
    });
  };





  // Mobile Card Renderer
  const renderMobileCard = (applicant, index) => (
    <CandidateCard key={index}>
      <CardContent>
        {/* Header with Serial Number + current status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Chip
            label={`#${index + 1}`}
            size="small"
            sx={{
              bgcolor: `${ACCENT}14`,
              color: ACCENT,
              fontWeight: 700,
              border: 'none',
            }}
          />
          {(() => {
            const s = getCurrentStatus(applicant);
            return (
              <Chip
                label={s.label}
                size="small"
                sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, border: 'none' }}
              />
            );
          })()}
        </Box>

        {/* Name with Avatar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: ACCENT,
              width: 48,
              height: 48,
            }}
          >
            {applicant.name?.charAt(0).toUpperCase() || 'N'}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: '600', fontSize: '16px' }}>
              {applicant.name || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {applicant.role || 'N/A'}
            </Typography>
          </Box>
        </Box>

        {/* Contact Information */}
        <InfoRow>
          <EmailIcon sx={{ fontSize: 18, color: '#666' }} />
          <Typography variant="body2">{applicant.email_address || 'N/A'}</Typography>
        </InfoRow>

        <InfoRow>
          <PhoneIcon sx={{ fontSize: 18, color: '#666' }} />
          <Typography variant="body2">{applicant.phone_number || 'N/A'}</Typography>
        </InfoRow>

        <InfoRow>
          <CalendarTodayIcon sx={{ fontSize: 18, color: '#666' }} />
          <Typography variant="body2">
            {applicant.interview_date
              ? formatDate(applicant.interview_date)
              : 'N/A'}
          </Typography>
        </InfoRow>

        {/* Interviewer Details */}
        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #eef0f4' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Interviewer Details
          </Typography>
          <InfoRow>
            <PersonIcon sx={{ fontSize: 18, color: '#666' }} />
            <Typography variant="body2">{applicant.interviewer_name || 'N/A'}</Typography>
          </InfoRow>
          <InfoRow>
            <EmailIcon sx={{ fontSize: 18, color: '#666' }} />
            <Typography variant="body2">{applicant.interviewemail || 'N/A'}</Typography>
          </InfoRow>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {applicant.resume && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DescriptionIcon />}
              href={applicant.resume}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ...OUTLINED_BTN, flex: 1 }}
            >
              Resume
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<EditIcon />}
            onClick={() => handleOpen(applicant)}
            sx={{ ...GRADIENT_BTN, flex: 1 }}
          >
            Update Status
          </Button>
          <IconButton
            onClick={() => handleDelete(applicant)}
            aria-label="Delete candidate"
            sx={{ color: '#dc2626', border: '1px solid #fee2e2', borderRadius: '12px' }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </CardContent>
    </CandidateCard>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <EventIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                  Scheduled Candidates
                </Typography>
                <Chip
                  label={scheduledOnly.length}
                  size="small"
                  sx={{ fontWeight: 700, bgcolor: `${ACCENT}14`, color: ACCENT, border: 'none' }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Candidates in the active interview process — scheduled and in later rounds.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            sx={GRADIENT_BTN}
          >
            Add Candidate
          </Button>
        </Stack>
      </Paper>

      {scheduledOnly.length > 0 ? (
        <>
          {isMobile ? (
            <Box sx={{ maxHeight: '70vh', overflowY: 'auto', p: 1 }}>
              {scheduledOnly.map((applicant, index) =>
                renderMobileCard(applicant, index)
              )}
            </Box>
          ) : (
            <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1, sm: 1.5 } }}>
            <TableWrap style={{ maxHeight: '70vh', overflowY: 'auto', borderRadius: '18px' }}>
              <table style={TABLE_STYLE}>
                <thead>
                  <tr>
                    <th style={TH}>S.No</th>
                    <th style={TH}>Name</th>
                    <th style={TH}>Role</th>
                    <th style={TH}>Email</th>
                    <th style={TH}>Contact No</th>
                    <th style={TH}>Interview Date</th>
                    <th style={TH}>Status</th>
                    <th style={TH}>Interviewer</th>
                    <th style={TH}>Meeting Link</th>
                    <th style={TH}>HR CC</th>
                    <th style={TH}>Resume</th>
                    <th style={TH_CENTER}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledOnly.map((applicant, index) => (
                    <Row key={index}>
                      <td style={TD}>
                        <Chip label={index + 1} size="small" sx={CHIP_SX} />
                      </td>
                      <td style={TD}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={AVATAR_SX}>
                            {applicant.name?.charAt(0).toUpperCase() || 'N'}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {applicant.name || 'N/A'}
                          </Typography>
                        </Box>
                      </td>
                      <td style={TD}>{applicant.role || 'N/A'}</td>
                      <td style={TD}>{applicant.email_address || 'N/A'}</td>
                      <td style={TD}>{applicant.phone_number || 'N/A'}</td>
                      <td style={TD}>
                        {applicant.interview_date
                          ? formatDate(applicant.interview_date)
                          : 'N/A'}
                      </td>
                      <td style={TD}>
                        {(() => {
                          const s = getCurrentStatus(applicant);
                          return (
                            <Chip
                              label={s.label}
                              size="small"
                              sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, border: 'none' }}
                            />
                          );
                        })()}
                      </td>
                      <td style={TD}>
                        <Box>
                          <Typography variant="body2">{applicant.interviewer_name || 'N/A'}</Typography>
                          <Typography variant="caption" color="text.secondary">{applicant.interviewemail}</Typography>
                        </Box>
                      </td>
                      <td style={TD}>
                        {applicant.interview_mode === 'face_to_face' ? (
                          <span title={applicant.interview_venue || ''} style={{ color: '#555' }}>
                            {applicant.interview_venue || 'Face-to-Face'}
                          </span>
                        ) : applicant.manual_meeting_link || applicant.zoom_meeting_link ? (
                          <a href={applicant.manual_meeting_link || applicant.zoom_meeting_link} target="_blank" rel="noreferrer" style={{ color: ACCENT, fontWeight: 600, textDecoration: 'none' }}>Join</a>
                        ) : 'N/A'}
                      </td>
                      <td style={TD}>{applicant.hr_cc_email || 'N/A'}</td>
                      <td style={TD}>
                        {applicant.resume ? (
                          <Button
                            size="small"
                            startIcon={<DescriptionIcon />}
                            href={applicant.resume}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT, bgcolor: `${ACCENT}14`, borderRadius: '10px', '&:hover': { bgcolor: `${ACCENT}22` } }}
                          >
                            Resume
                          </Button>
                        ) : 'N/A'}
                      </td>
                      <td style={TD_CENTER}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handleOpen(applicant)}
                            sx={{ ...OUTLINED_BTN, fontSize: '12px' }}
                          >
                            Update Status
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(applicant)}
                            aria-label="Delete candidate"
                            sx={{ color: '#dc2626', border: '1px solid #fee2e2', borderRadius: '10px', '&:hover': { bgcolor: '#fef2f2', borderColor: '#fecaca' } }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </td>
                    </Row>
                  ))}
                </tbody>
              </table>
            </TableWrap>
            </Paper>
          )}
        </>
      ) : (
        <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 6 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EventIcon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>No Scheduled Interviews</Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            Scheduled candidates will be listed here once available.
          </Typography>
        </Paper>
      )}
      <ProgressModel selectedCan={selectedCan} open={open} setOpen={setOpen} />
      <SheduleBox open={addOpen} close={() => setAddOpen(false)} />
    </Box>
  );
};

export default ScheduledCandidate;